import { asteroidField } from "./asteroid-field";
import {
	ASTEROID_SPAWN_INTERVAL,
	OBJECTIVE_SHIPS,
	RANDOM_SPAWN_PLANET_CLEARANCE,
	RANDOM_SPAWN_SPACING,
	SHIP_LIVES,
	SHIP_RADIUS,
	SPAWN_POINT_RADIUS,
	SURVIVAL_2008_SHIPS,
} from "./constants";
import {
	createPlanet,
	createSpawnPoint,
	type Entity,
	type EntityId,
	type PlayerIndex,
	type ShipColor,
} from "./entities";
import type { SimEvent } from "./events";
import type { MapDef } from "./maps";
import { CLASSIC_RULES, type MatchRules, normalizeRules } from "./match-rules";
import { createRng, nextFloat, type RngState } from "./math/rng";
import { type WorldSize, wrappedDistance } from "./math/torus";
import type { Vec2 } from "./math/vec2";
import type { GameMode, PlayerState, WorldState } from "./state";
import { createObjective } from "./systems/objectives";

export interface WorldOptions {
	/** 1 (survival), 2 (the original), or 3–4 for free-for-all (P11.3). */
	playerCount: 1 | 2 | 3 | 4;
	seed: number;
	/** Optional gameplay rules; defaults to the original (classic) behaviour. */
	rules?: MatchRules;
	/** Defaults to duel. */
	mode?: GameMode;
	/** Survival only: "2008" = the original spec's scoring (P11.5); default asteroids destroyed. */
	survivalScoring?: "asteroids" | "2008";
}

/** legacy: TwoPlayersPlayState.cpp — player 1 flies blue, player 2 flies red; then green, yellow. */
export const PLAYER_COLORS: readonly ShipColor[] = ["blue", "red", "green", "yellow"];

/**
 * Builds a world from a map: planets and spawn points in map order, one PlayerState per
 * player, and each player's first ship queued (legacy Player::init → tryRespawnShip).
 */
export function createWorld(map: MapDef, options: WorldOptions): WorldState {
	// Lasers may fly at most twice the map's longest side.
	const maxLaserRange = 2 * Math.max(map.size.w, map.size.h);
	const rules = normalizeRules(options.rules ?? CLASSIC_RULES, CLASSIC_RULES, maxLaserRange);
	const world: WorldState = {
		tick: 0,
		size: { width: map.size.w, height: map.size.h },
		rules,
		mode: options.mode ?? "duel",
		rng: createRng(options.seed),
		nextId: 1,
		entities: [],
		removeQueue: [],
		shipQueue: [],
		asteroidQueue: [],
		asteroidCountdown: ASTEROID_SPAWN_INTERVAL,
		pickupCountdown: rules.powerUpIntervalMinTicks,
		players: [],
		objective: null,
	};
	if (map.asteroids) {
		const field = asteroidField(map.asteroids);
		world.asteroidField = field;
		world.asteroidCountdown = field.interval ?? 0;
	}

	for (const p of map.planets) {
		world.entities.push(createPlanet(allocId(world), p.type, p, p.radius, p.mass));
	}
	// Classic: the map's spawn points (and no random draws, so legacy runs stay identical).
	let spawnPoints =
		rules.spawnPoints === null
			? map.spawnPoints
			: randomSpawnPoints(world.rng, world.size, map, rules.spawnPoints);
	// More pilots than spawn points (3–4 players on a two-point map): add random ones. Only
	// then are random numbers drawn, so classic two-player runs stay identical.
	// (An explicit random count from the rules is respected: ships then take turns.)
	if (rules.spawnPoints === null && spawnPoints.length < options.playerCount) {
		const extra = randomSpawnPoints(
			world.rng,
			world.size,
			{ ...map, spawnPoints },
			options.playerCount - spawnPoints.length,
		);
		spawnPoints = [...spawnPoints, ...extra];
	}
	for (const sp of spawnPoints) {
		world.entities.push(createSpawnPoint(allocId(world), sp));
	}
	// I-026: fueling stations only matter with the fuel rule (no ids, so other ids don't shift).
	if (rules.fuel && rules.fuelStations && map.fuelItems.length > 0) {
		world.fuelStations = map.fuelItems.map((f) => ({
			pos: { x: f.x, y: f.y },
			rate: f.amount,
		}));
	}

	for (let index = 0; index < options.playerCount; index++) {
		const player: PlayerState = {
			index,
			color: PLAYER_COLORS[index % PLAYER_COLORS.length] ?? "blue",
			shipId: null,
			awaitingSpawn: false,
			shipsLeft:
				options.mode === "survival" && options.survivalScoring === "2008"
					? SURVIVAL_2008_SHIPS
					: options.mode === "koth" ||
							options.mode === "ctf" ||
							((options.mode ?? "duel") === "duel" && rules.scoreLimit !== null)
						? OBJECTIVE_SHIPS
						: SHIP_LIVES,
			respawnCountdown: 0,
			shipKills: 0,
			asteroidKills: 0,
		};
		world.players.push(player);
		queueShip(world, player, []);
	}
	world.objective = createObjective(world, map, options.survivalScoring);

	return world;
}

/**
 * `count` random spawn points clear of planet surfaces and spread out. The spacing (then the
 * planet clearance) relaxes step by step on crowded maps, so this always returns `count`.
 */
export function randomSpawnPoints(
	rng: RngState,
	size: WorldSize,
	map: MapDef,
	count: number,
): Vec2[] {
	const points: Vec2[] = [];
	let spacing = RANDOM_SPAWN_SPACING;
	let clearance = RANDOM_SPAWN_PLANET_CLEARANCE;
	for (let attempt = 1; points.length < count; attempt++) {
		const p = { x: nextFloat(rng) * size.width, y: nextFloat(rng) * size.height };
		const clearOfPlanets = map.planets.every(
			(planet) => wrappedDistance(p, planet, size) > planet.radius + clearance,
		);
		const spaced = points.every((q) => wrappedDistance(p, q, size) > spacing);
		if (clearOfPlanets && spaced) points.push(p);
		if (attempt % 200 === 0) {
			spacing *= 0.75;
			// Never closer to a surface than a ship needs to spawn without touching it.
			clearance = Math.max(SHIP_RADIUS + SPAWN_POINT_RADIUS, clearance * 0.75);
		}
		if (attempt > 20_000) points.push(p); // pathological map: take what we get
	}
	return points;
}

export function allocId(world: WorldState): EntityId {
	return world.nextId++;
}

/** Binary search: entities are kept sorted by id because ids only grow and we only append. */
export function findEntity(world: WorldState, id: EntityId): Entity | undefined {
	const list = world.entities;
	let lo = 0;
	let hi = list.length - 1;
	while (lo <= hi) {
		const mid = (lo + hi) >> 1;
		const e = list[mid];
		if (e === undefined) return undefined;
		if (e.id === id) return e;
		if (e.id < id) lo = mid + 1;
		else hi = mid - 1;
	}
	return undefined;
}

/** Adds a newly created entity (its id must come from allocId) and emits `inserted`. */
export function insertEntity(world: WorldState, entity: Entity, events: SimEvent[]): void {
	world.entities.push(entity);
	events.push({ type: "inserted", id: entity.id, kind: entity.kind });
}

/** Queues removal at the start of the next tick (legacy WorldObjectsManager::queueRemoval). */
export function queueRemoval(world: WorldState, id: EntityId): void {
	if (!world.removeQueue.includes(id)) world.removeQueue.push(id);
}

export function isQueuedForRemoval(world: WorldState, id: EntityId): boolean {
	return world.removeQueue.includes(id);
}

export function getPlayer(world: WorldState, index: PlayerIndex): PlayerState | undefined {
	return world.players[index];
}

/** Queues the player's next ship if any are left (legacy Player::tryRespawnShip). */
export function queueShip(world: WorldState, player: PlayerState, events: SimEvent[]): boolean {
	if (player.shipsLeft <= 0) return false;
	player.shipsLeft--;
	player.awaitingSpawn = true;
	world.shipQueue.push(player.index);
	events.push({ type: "shipQueued", player: player.index, shipsLeft: player.shipsLeft });
	return true;
}
